import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Route as RouteIcon,
  User,
  MapPin,
  Clock,
  Package,
  Search,
  UserCheck,
  Truck,
  Calendar,
  Navigation,
} from 'lucide-react';
import { getAllRoutes, assignDriver, type Route } from '@/api/routeService';
import { getAllDrivers } from '@/api/pharmacyService';
import { getPickupOrders } from '@/api/pickupService';
import axiosInstance from '@/api/axiosInstance';
import GlobalLoader from '../../components/ui/GlobalLoader';

interface Driver {
  _id?: string;
  id?: string;
  userId?: string;
  name?: string;
  fullName?: string;
  email?: string;
  totalDeliveries?: number;
  phone?: string;
  status?: 'online' | 'busy' | 'offline';
  currentLocation?: {
    latitude: number;
    longitude: number;
  };
  vehicleType?: string;
  vehicleNumber?: string;
}

export function AssignRoutesPage() {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'pickup-orders' | 'orders'>('pickup-orders');

  useEffect(() => {
    fetchRoutes();
    fetchDrivers();
  }, []);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const response = await getAllRoutes({ limit: 100 });
      const routesData = Array.isArray(response.data) 
        ? response.data 
        : response.data ? [response.data] : [];
      setRoutes(routesData as Route[]);
    } catch (error) {
      console.error('Error fetching routes:', error);
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response: any = await getAllDrivers();
      console.log('Drivers response:', response);
      
      // Handle different response structures
      let driversData: Driver[] = [];
      if (response.data?.drivers) {
        driversData = response.data.drivers;
      } else if (response.drivers) {
        driversData = response.drivers;
      } else if (Array.isArray(response.data)) {
        driversData = response.data;
      } else if (Array.isArray(response)) {
        driversData = response;
      }
      
      setDrivers(driversData);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('Failed to load drivers');
    }
  };

  const handleAssignRoute = (route: Route) => {
    setSelectedRoute(route);
    // Extract driver ID from assignedDriver object (if exists)
    const driverId: string = route.assignedDriver?._id || '';
    
    setSelectedDriverId(driverId);
    setAssignDialogOpen(true);
  };

  const confirmAssignment = async () => {
    if (!selectedRoute || !selectedDriverId) {
      toast.error('Please select a rider');
      return;
    }

    try {
      setLoading(true);
      const routeId = selectedRoute._id || selectedRoute.id || selectedRoute.routeId;
      
      if (!routeId) {
        toast.error('Invalid route ID');
        return;
      }

      // Import delivery service dynamically
      const { deliveryService } = await import('../../api/deliveryService');

      // Different logic based on active tab
      if (activeTab === 'pickup-orders') {
        // PICKUP ORDERS TAB: Assign route to rider and then assign pickup orders
        
        // Step 1: Assign driver to the route first
        console.log('📍 Step 1: Assigning route to rider:', { routeId, driverId: selectedDriverId });
        await assignDriver(routeId as string, selectedDriverId);
        
        // Debug: Log the entire selected route
        console.log('🔍 DEBUG: selectedRoute:', selectedRoute);
        console.log('🔍 DEBUG: selectedRoute.waypoints:', selectedRoute.waypoints);
        
        // Extract pharmacy IDs from route waypoints
        const pharmacyIds = selectedRoute.waypoints
          ?.map(wp => wp.pharmacy?._id)
          .filter(Boolean) || [];
        
        console.log('🏥 Found pharmacy IDs in route waypoints:', pharmacyIds);
        
        // Fetch pickup orders for these pharmacies
        let pickupOrderIds: string[] = [];
        if (pharmacyIds.length > 0) {
          try {
            const pickupOrdersResponse = await getPickupOrders({ limit: 100 });
            console.log('📦 Fetched pickup orders:', pickupOrdersResponse.data);
            
            // Filter pickup orders by pharmacy IDs from route waypoints
            pickupOrderIds = pickupOrdersResponse.data
              .filter(po => pharmacyIds.includes(po.pharmacyId))
              .map(po => po._id);
            
            console.log('🎯 Matching pickup order IDs:', pickupOrderIds);
          } catch (error) {
            console.error('❌ Error fetching pickup orders:', error);
          }
        }
        
        console.log('📦 Final pickup order IDs to assign:', pickupOrderIds);
        
        if (pickupOrderIds.length > 0) {
          // Assign each pickup order to the rider using the same logic as PickupOrdersAdminPage
          const assignmentPromises = pickupOrderIds.map(pickupOrderId => {
            console.log('📤 Assigning pickup order to rider:', {
              pickupOrderId,
              driverId: selectedDriverId,
              routeId: routeId as string,
              routeName: selectedRoute.name || 'Assigned Route'
            });
            
            return deliveryService.assignOrderToDriver(
              pickupOrderId,
              {
                driverId: selectedDriverId, // This is the rider ID the backend needs
                routeName: selectedRoute.name || 'Assigned Route',
                routeId: routeId as string,
                deliveryCharges: 0 // Default or can be calculated
              }
            );
          });
          
          await Promise.all(assignmentPromises);
          console.log(`✅ Successfully assigned ${pickupOrderIds.length} pickup order(s) to rider with ID: ${selectedDriverId}`);
        } else {
          console.log('⚠️ No pickup orders found for route pharmacies');
        }
        
        // toast.success(
        //   pickupOrderIds.length > 0 
        //     ? `Route assigned to rider and ${pickupOrderIds.length} pickup order(s) assigned successfully`
        //     : 'Route assigned to rider successfully'
        // );
      } else {
        // ORDERS TAB: Assign individual orders from route waypoints to rider (OrderProcessingPage logic)
        
        // Step 1: Assign route to rider
        console.log('📍 Step 1: Assigning route to rider:', { routeId, driverId: selectedDriverId });
        await assignDriver(routeId as string, selectedDriverId);
        
        // Step 2: Extract order IDs from route waypoints
        const orderIds = selectedRoute.waypoints
          ?.map(wp => wp.orderId)
          .filter(Boolean) || [];
        
        console.log('📦 Found order IDs in route waypoints:', orderIds);
        
        if (orderIds.length > 0) {
          // Assign each order to the rider (same logic as OrderProcessingPage)
          const assignmentPromises = orderIds.map(orderId => {
            console.log('📤 Assigning order to rider:', {
              orderId,
              driverId: selectedDriverId,
              routeName: selectedRoute.name || 'Assigned Route'
            });
            
            return deliveryService.assignOrderToDriver(
              orderId,
              {
                driverId: selectedDriverId,
                routeName: selectedRoute.name || 'Assigned Route',
                deliveryCharges: 0 // Default delivery charges
              }
            );
          });
          
          await Promise.all(assignmentPromises);
          console.log(`✅ Successfully assigned ${orderIds.length} order(s) to rider with ID: ${selectedDriverId}`);
          
          // toast.success(`Route assigned to rider and ${orderIds.length} order(s) assigned successfully`);
        } else {
          console.log('⚠️ No orders found in route waypoints');
          toast.success('Route assigned to rider successfully');
        }
      }
      
      setAssignDialogOpen(false);
      setSelectedRoute(null);
      setSelectedDriverId('');
      fetchRoutes();
    } catch (error: any) {
      console.error('Error assigning route to rider:', error);
      toast.error(error?.response?.data?.message || 'Failed to assign route to rider');
    } finally {
      setLoading(false);
    }
  };

  const getDriverName = (driverId?: string) => {
    if (!driverId) return 'Unassigned';
    const driver = drivers.find(d => (d._id || d.id || d.userId) === driverId);
    return driver?.name || driver?.fullName || 'Unknown Driver';
  };

  const getDriverStatus = (driverId?: string) => {
    if (!driverId) return null;
    const driver = drivers.find(d => (d._id || d.id || d.userId) === driverId);
    return driver?.status ;
  };

  // Filter routes based on active tab and other filters
  const filteredRoutes = routes.filter(route => {
    const matchesSearch = route.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         route.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || route.status === statusFilter;
    
    // Filter by active tab
    const matchesTab = activeTab === 'pickup-orders' 
      ? (route.routeType === 'pickup-orders' || !route.routeType) // Include routes without routeType for backward compatibility
      : route.routeType === 'orders';
    
    return matchesSearch && matchesStatus && matchesTab;
  });

  // Get driver assignment details
  const getDriverRoutes = (driverId: string) => {
    return routes.filter(route => route.assignedDriver?._id === driverId);
  };

  const getDriverAssignedRoutesCount = (driverId: string) => {
    return getDriverRoutes(driverId).length;
  };

  const getDriverPickupOrdersCount = (driverId: string) => {
    return getDriverRoutes(driverId).filter(r => r.routeType === 'pickup-orders' || !r.routeType).length;
  };

  const getDriverDeliveryOrdersCount = (driverId: string) => {
    return getDriverRoutes(driverId).filter(r => r.routeType === 'orders').length;
  };

  const isDriverAssigned = (driverId: string) => {
    return routes.some(route => route.assignedDriver?._id === driverId);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDriverStatusColor = (status?: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-100 text-green-800';
      case 'busy':
        return 'bg-orange-100 text-orange-800';
      case 'offline':
        return 'bg-red-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleVehicleTypeChange = async (driverId: string, vehicleType: string) => {
    try {
      // Update driver vehicle type
      const baseEndpoint = import.meta.env.VITE_API_BASE_URL;
      const payload = {
        vehicleType: vehicleType,
        riderId: driverId
      }
      const response = await axiosInstance.put(`${baseEndpoint}/admin/riders/vehicle`, payload)
      // const response = await fetch(, {
      //   method: 'PUT',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${localStorage.getItem('token')}`
      //   },
      //   body: JSON.stringify({ payload })
      // });

      // if (response.ok) {
      //   toast.success('Vehicle type updated successfully');
      //   // Update local state
      //   setDrivers(prev => prev.map(driver => 
      //     driver._id === driverId 
      //       ? { ...driver, vehicleType }
      //       : driver
      //   ));
      // } else {
      //   toast.error('Failed to update vehicle type');
      // }
    } catch (error: any) {
      toast.error('Error updating vehicle type: ' + error.message);
    }
  };

  const renderContent = () => (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Routes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredRoutes.length}</div>
          </CardContent>
        </Card>
        {/* <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Routes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {filteredRoutes.filter(r => r.status === 'active').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Assigned Routes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {filteredRoutes.filter(r => r.assignedDriver).length}
            </div>
          </CardContent>
        </Card> */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Online Drivers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {drivers.filter(d => d.status === 'online').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div>
              <Label htmlFor="search">Search Routes</Label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11"
                />
              </div>
            </div>
            {/* <div>
              <Label htmlFor="status" className='mb-2'>Status Filter</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger id="status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div> */}
          </div>
        </CardContent>
      </Card>

      {/* Routes List */}
      <Card>
        <CardHeader>
          <CardTitle>Routes ({filteredRoutes.length})</CardTitle>
          <CardDescription>Click "Assign Rider" to assign a route to a rider</CardDescription>
        </CardHeader>
        <CardContent>
          {loading && routes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Loading routes...</div>
          ) : filteredRoutes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <RouteIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No routes found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRoutes.map((route) => (
                <Card key={route._id || route.id || route.routeId} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-gray-900">
                            {route.name || 'Untitled Route'}
                          </h3>
                          <Badge className={getStatusColor(route.status)}>
                            {route.status || 'inactive'}
                          </Badge>
                          {route.routeType && (
                            <Badge variant="outline">
                              {route.routeType === 'pickup-orders' ? '📦 Pickup' : '🚚 Delivery'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          {route.description || 'No description'}
                        </p>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-700">
                              {route.waypoints?.length || 0} waypoints
                            </span>
                          </div>
                          {route.distance ? (
                            <div className="flex items-center gap-2">
                              <Navigation className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-700">
                                {route.distance.toFixed(2)} km
                              </span>
                            </div>
                          ): null}
                          {route.estimatedTime ? (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-700">
                                {Math.round(route.estimatedTime)} min
                              </span>
                            </div>
                          ): null}
                          {route.createdAt && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-700">
                                {new Date(route.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Driver Assignment Info */}
                        <div className="mt-3 pt-3 border-t">
                          {route.assignedDriver ? (
                            <div className="flex items-center gap-2">
                              <UserCheck className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-gray-900">
                                Assigned to: {route.assignedDriver.name}
                              </span>
                              {route.assignedDriver.status && (
                                <Badge className={getDriverStatusColor(route.assignedDriver.status)}>
                                  {route.assignedDriver.status}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-orange-600">
                              <User className="w-4 h-4" />
                              <span className="text-sm font-medium">Not assigned</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="ml-4">
                        <Button
                          onClick={() => handleAssignRoute(route)}
                          variant={route.assignedDriver ? "outline" : "default"}
                          size="sm"
                          className={!route.assignedDriver ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
                          disabled={route.assignedDriver ? true : false}
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          {/* {route.assignedDriver ? 'Reassign Rider' : 'Assign Rider'} */}
                          {route.assignedDriver ? 'Assigned' : 'Assign Rider'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Riders Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Riders / Drivers ({drivers.length})</CardTitle>
              <CardDescription>View all drivers and their assignment details. Drivers can be assigned multiple routes.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {drivers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <User className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No drivers found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {drivers.map((driver) => {
                const driverId = driver._id || driver.id || driver.userId || '';
                const driverName = driver.name || driver.fullName || 'Unknown Driver';
                const driverStatus = driver.status || 'offline';
                const assignedRoutes = getDriverAssignedRoutesCount(driverId);
                const pickupOrders = getDriverPickupOrdersCount(driverId);
                const deliveryOrders = getDriverDeliveryOrdersCount(driverId);
                const isAssigned = isDriverAssigned(driverId);
                
                return (
                  <Card key={driverId} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{driverName}</h4>
                            <p className="text-xs text-gray-500">{driver.email}</p>
                          </div>
                        </div>
                        <Badge className={getDriverStatusColor(driverStatus)}>
                          {driverStatus}
                        </Badge>
                      </div>

                      <div className="space-y-2">
                        {/* Vehicle Info - Editable */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Vehicle:</span>
                          <Select 
                            value={driver.vehicleType || 'bike'} 
                            onValueChange={(value) => handleVehicleTypeChange(driver._id, value)}
                          >
                            <SelectTrigger className="w-32 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="🏍️">🏍️ Bike</SelectItem>
                              <SelectItem value="🚗">🚗 Car</SelectItem>
                              <SelectItem value="🚚">🚚 Truck</SelectItem>
                              <SelectItem value="🚐">🚐 Van</SelectItem>
                              {/* <SelectItem value="🐪">🐪 Camel</SelectItem> */}
                            </SelectContent>
                          </Select>
                        </div>
                        {driver.vehicleNumber && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Number:</span>
                            <span className="font-medium text-gray-900">{driver.vehicleNumber}</span>
                          </div>
                        )}

                        {/* Phone */}
                        {driver.phone && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Phone:</span>
                            <span className="font-medium text-gray-900">{driver.phone}</span>
                          </div>
                        )}

                        {/* Assignment Stats */}
                        <div className="pt-3 border-t mt-3">
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div>
                              <div className="text-lg font-bold text-blue-600">{assignedRoutes}</div>
                              <div className="text-xs text-gray-500">Routes</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-orange-600">{pickupOrders}</div>
                              <div className="text-xs text-gray-500">Pickups</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-purple-600">{deliveryOrders}</div>
                              <div className="text-xs text-gray-500">Deliveries</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-green-600">{driver.totalDeliveries}</div>
                              <div className="text-xs text-gray-500">Completed</div>
                            </div>
                          </div>
                        </div>

                        {/* Assigned Routes List */}
                        {assignedRoutes > 0 && (
                          <div className="pt-3 border-t mt-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">Assigned Routes:</p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {getDriverRoutes(driverId).map((route) => (
                                <div key={route._id || route.id} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                                  <span className="font-medium truncate">{route.name}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {route.routeType === 'pickup-orders' ? '📦' : '🚚'}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
          <p className="text-gray-500">Assign routes to available drivers</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'pickup-orders' | 'orders')} className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="pickup-orders"
            className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            <Package className="w-4 h-4" />
            Pickup Orders
          </TabsTrigger>
          <TabsTrigger value="orders"
            className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            <Truck className="w-4 h-4" />
            Orders
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pickup-orders" className="space-y-6 mt-6">
          {renderContent()}
        </TabsContent>

        <TabsContent value="orders" className="space-y-6 mt-6">
          {renderContent()}
        </TabsContent>
      </Tabs>

      {/* Assign Driver Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-[600px] bg-white">
          <DialogHeader>
            <DialogTitle>Assign Rider to Route</DialogTitle>
            <DialogDescription>
              Select a rider to assign to "{selectedRoute?.name}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Route Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-sm text-gray-900 mb-2">Route Details</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>{selectedRoute?.waypoints?.length || 0} waypoints</span>
                </div>
                {selectedRoute?.distance && (
                  <div className="flex items-center gap-2">
                    <Navigation className="w-4 h-4 text-gray-500" />
                    <span>{selectedRoute.distance.toFixed(2)} km</span>
                  </div>
                )}
                {selectedRoute?.estimatedTime && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span>{Math.round(selectedRoute.estimatedTime)} min</span>
                  </div>
                )}
                {selectedRoute?.routeType && (
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-500" />
                    <span>{selectedRoute.routeType === 'pickup-orders' ? 'Pickup' : 'Delivery'}</span>
                  </div>
                )}
              </div>
              
              {/* Show orders count based on active tab */}
              {selectedRoute && selectedRoute.waypoints && (
                <>
                  {(() => {
                    if (activeTab === 'pickup-orders') {
                      // For pickup-orders tab, show pharmacy count (not order count in waypoints)
                      const pharmacyCount = selectedRoute.waypoints.filter(wp => wp.pharmacy).length;
                      return pharmacyCount > 0 ? (
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex items-center gap-2 text-sm bg-blue-50 p-2 rounded">
                            <Package className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-blue-900">
                              Pickup orders from {pharmacyCount} pharmacy(ies) will be assigned to rider
                            </span>
                          </div>
                        </div>
                      ) : null;
                    } else {
                      // For orders tab, show order count
                      const orderCount = selectedRoute.waypoints.filter(wp => wp.orderId).length;
                      return orderCount > 0 ? (
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex items-center gap-2 text-sm bg-green-50 p-2 rounded">
                            <Package className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-green-900">
                              {orderCount} order(s) will be assigned to rider
                            </span>
                          </div>
                        </div>
                      ) : null;
                    }
                  })()}
                </>
              )}
            </div>

            {/* Driver Selection */}
            <div>
              <Label htmlFor="driver-select">Select Rider</Label>
              <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
                <SelectTrigger id="driver-select">
                  <SelectValue placeholder="Choose a rider..." />
                </SelectTrigger>
                <SelectContent>
                  {drivers.length === 0 ? (
                    <SelectItem value="none" disabled>No riders available</SelectItem>
                  ) : (
                    drivers.map((driver) => {
                      const driverId = driver._id || driver.id || driver.userId || '';
                      const driverName = driver.name || driver.fullName || 'Unknown Driver';
                      const driverStatus = driver.status || 'offline';
                      
                      return (
                        <SelectItem key={driverId} value={driverId}>
                          <div className="flex items-center justify-between w-full">
                            <span>{driverName}</span>
                            <Badge className={`ml-2 ${getDriverStatusColor(driverStatus)}`}>
                              {driverStatus}
                            </Badge>
                          </div>
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* All Riders List */}
            <div>
              <Label className="mb-2 block">All Riders ({drivers.length})</Label>
              <div className="max-h-48 overflow-y-auto border rounded-lg">
                {drivers.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No riders found
                  </div>
                ) : (
                  <div className="divide-y">
                    {drivers.map((driver) => {
                      const driverId = driver._id || driver.id || driver.userId || '';
                      const driverName = driver.name || driver.fullName || 'Unknown Driver';
                      const driverStatus = driver.status || 'offline';
                      
                      return (
                        <div
                          key={driverId}
                          className={`p-3 hover:bg-gray-50 cursor-pointer ${
                            selectedDriverId === driverId ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => setSelectedDriverId(driverId)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{driverName}</p>
                                <p className="text-xs text-gray-500">
                                  {driver.vehicleType || 'Vehicle'} • {driver.vehicleNumber || 'N/A'}
                                </p>
                              </div>
                            </div>
                            <Badge className={getDriverStatusColor(driverStatus)}>
                              {driverStatus}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmAssignment}
              disabled={!selectedDriverId || loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? 'Assigning...' : 'Assign Rider'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global loading overlay */}
      <GlobalLoader 
        isLoading={loading}
        message="Loading assignment data..."
        subMessage="Please wait while we fetch routes and drivers"
      />
    </div>
  );
}

